<template>
  <div class="app-links">
    <h2 :style="{ color: colors.primaryText }">Get the {{ APP_NAME }} App</h2>

    <ul class="app-links__list">
      <li v-if="showIos">
        <a
          :href="APP_STORE_URL"
          class="app-links__badge"
          :class="{ 'app-links__badge--dark': isDark }"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="`Download ${APP_NAME} on the App Store (opens in new tab)`"
          @click="trackDownload('ios')"
        >
          <img
            src="/badges/app-store.svg"
            width="120"
            height="40"
            loading="lazy"
            alt="Download on the App Store"
          />
        </a>
      </li>
      <li v-if="showAndroid">
        <a
          :href="PLAY_STORE_URL"
          class="app-links__badge"
          :class="{ 'app-links__badge--dark': isDark }"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="`Get ${APP_NAME} on Google Play (opens in new tab)`"
          @click="trackDownload('android')"
        >
          <img
            src="/badges/google-play.png"
            width="134"
            height="40"
            loading="lazy"
            alt="Get it on Google Play"
          />
        </a>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useTheme } from "~/composables/useTheme";
import { useDevicePlatform } from "~/composables/useDevicePlatform";
import { APP_NAME, APP_STORE_URL, PLAY_STORE_URL } from "~/config/app.config";

const { colors, themeName } = useTheme();
const isDark = computed(() => themeName.value === "dark");

// Show only the store the visitor's device can actually install from.
// `platform` is "other" until mounted, so SSR and desktop both render every store.
const { platform } = useDevicePlatform();
const showIos = computed(() => platform.value !== "android");
const showAndroid = computed(() => platform.value !== "ios");

const trackDownload = (store: "ios" | "android") => {
  if (globalThis.window !== undefined) {
    const win = globalThis.window as any;
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({
      event: "app_download_click",
      store,
      device_platform: platform.value,
      location: "footer",
    });
  }
};
</script>

<style scoped>
/* .social-icons above already contributes a 50px bottom margin. */
.app-links {
  margin-top: 8px;
}

h2 {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 16px;
}

.app-links__list {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Official badge artwork must not be recoloured, so in dark mode the black
   badge is delineated with a border outside its clear space instead. */
.app-links__badge {
  display: inline-block;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: opacity 0.2s ease;
}

.app-links__badge--dark {
  border-color: rgba(255, 255, 255, 0.25);
}

.app-links__badge:hover {
  opacity: 0.85;
}

.app-links__badge img {
  display: block;
  height: 40px;
  width: auto;
}

/* On mobile this is the only download CTA, so keep it at a 44px touch target. */
@media (max-width: 480px) {
  .app-links__badge img {
    height: 44px;
  }
}
</style>
